import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAide } from './AideContexte';
import SchemaProcessus, { colonnesDe } from './SchemaProcessus';
import './Aide.css';

/**
 * LA FENÊTRE « COMPRENDRE LE PROCESSUS MÉTIER » — commune à tous les modules (étape A2)
 * =====================================================================================
 * Maquette : doc/maquettes/aide_contextuelle_maquette.html · Règles du schéma : charte graphique S1 → S13
 *
 * Rendue dans <body> (portail) : par-dessus la barre latérale et le bandeau collant du module.
 * N'affiche QUE les processus écrits (pas d'onglet vide). S'ouvre sur le processus de l'écran courant.
 * Un clic sur une étape montre son DÉTAIL ; « Aller à … » ferme la fenêtre et ouvre l'écran (S11).
 */
function FenetreProcessus() {
    const aide = useAide();
    const ouvert = !!(aide && aide.processusOuvert);
    const [proc, setProc] = useState(null);
    const [choisie, setChoisie] = useState(null);
    const donnees = aide ? aide.donnees : null;
    const ecran = aide ? aide.ecran : '';

    // À chaque ouverture : le processus de l'écran courant (ou le premier), aucune étape choisie.
    useEffect(() => {
        if (!ouvert || !donnees || !donnees.PROCESSUS) return;
        const cles = Object.keys(donnees.PROCESSUS);
        const duCourant = cles.find((k) => Object.values(donnees.PROCESSUS[k].etapes).some((e) => e.ecrans.includes(ecran)));
        setProc(duCourant || cles[0]);
        setChoisie(null);
    }, [ouvert, donnees, ecran]);

    if (!ouvert || !donnees || !donnees.PROCESSUS || !proc || !donnees.PROCESSUS[proc]) return null;
    const P = donnees.PROCESSUS[proc];
    const cles = Object.keys(donnees.PROCESSUS);
    const fermer = () => aide.setProcessusOuvert(false);

    const libelleEcran = (cle) => {
        const g = cle.split('/')[0];
        const e = donnees.ECRANS.find((x) => x.cle === cle);
        const grp = donnees.GROUPES[g] || g;
        return e && e.libelle !== grp ? `${grp} › ${e.libelle}` : grp;
    };
    const ici = Object.values(P.etapes).some((e) => e.ecrans.includes(ecran));
    const E = choisie != null ? P.etapes[choisie] : null;

    return createPortal(
        <div className="aide-ov" onMouseDown={(ev) => { if (ev.target === ev.currentTarget) fermer(); }}>
            <div className={'aide-fen' + (colonnesDe(P) > 6 ? ' large' : '')} role="dialog" aria-modal="true" aria-label="Comprendre le processus métier">
                <div className="aide-fen-h">
                    <i className="fa-solid fa-diagram-project aide-fen-ic" />
                    <span className="aide-fen-tt">Comprendre le processus métier</span>
                    <span className="aide-fen-ss">· {donnees.titreModule}</span>
                    <button type="button" className="aide-v-x" onClick={fermer} title="Fermer (Échap)" aria-label="Fermer"><i className="fa-solid fa-xmark" /></button>
                </div>
                {cles.length > 1 && (
                    <div className="aide-fen-tabs">
                        {cles.map((k) => (
                            <button key={k} type="button" className={k === proc ? 'on' : ''} onClick={() => { setProc(k); setChoisie(null); }}>
                                <i className={'fa-solid ' + donnees.PROCESSUS[k].icone} /> {donnees.PROCESSUS[k].titre}
                            </button>
                        ))}
                    </div>
                )}
                <div className="aide-fen-b">
                    <div className="aide-fen-ici">
                        <i className="fa-solid fa-location-dot" /> Vous êtes dans <b>{libelleEcran(ecran)}</b>
                        {' — '}{ici ? <>les étapes encadrées en vert (<i className="fa-solid fa-location-dot" />) se traitent sur cet écran.</> : 'cet écran ne traite aucune étape de ce processus.'}
                        {' '}Cliquez une étape pour le détail.
                    </div>
                    <div className="aide-fen-titre"><i className={'fa-solid ' + P.icone} /> Processus « {P.titre} »</div>
                    <SchemaProcessus processus={P} acteurs={donnees.ACTEURS} ecran={ecran} choisie={choisie} onChoisir={setChoisie} />
                    {E && (
                        <div className="aide-detail">
                            <div className="aide-detail-num">
                                {E.sortie ? <i className="fa-solid fa-right-from-bracket" /> : E.prealable ? <i className="fa-solid fa-gear" /> : E.icone ? <i className={'fa-solid ' + E.icone} /> : choisie}
                            </div>
                            <div>
                                <div className="aide-detail-tt">{E.libelle}</div>
                                <div className="aide-detail-qui">
                                    Qui agit : {E.acteurs.map((a) => donnees.ACTEURS[a].libelle).join(', ')} · Où : {E.ou}
                                </div>
                                <div className="aide-detail-tx">{E.texte}</div>
                            </div>
                            {E.ecrans.includes(ecran) ? (
                                <span className="aide-detail-ici"><i className="fa-solid fa-location-dot" /> sur cet écran</span>
                            ) : aide.raison(E.ecrans[0]) ? (
                                <span className="aide-v-pas"><i className="fa-solid fa-circle-info" /> {libelleEcran(E.ecrans[0])} : {aide.raison(E.ecrans[0])}</span>
                            ) : (
                                <button type="button" className="aide-btn-aller" onClick={() => { fermer(); aide.ouvrir(E.ecrans[0]); }}>
                                    <i className="fa-solid fa-arrow-right" /> Aller à {libelleEcran(E.ecrans[0])}
                                </button>
                            )}
                        </div>
                    )}
                    <div className="aide-legende">
                        {[...new Set(Object.values(P.etapes).flatMap((e) => e.acteurs))].map((k) => donnees.ACTEURS[k]).map((a) => (
                            <span key={a.libelle}><span className="aide-leg-pt" style={{ background: a.couleur }} />{a.libelle}</span>
                        ))}
                        <span><span className="aide-leg-cadre" /> étape selon le cas (pointillé)</span>
                        <span><i className="fa-solid fa-rotate-right" /> relance possible</span>
                        <span><svg width="30" height="10" aria-hidden="true"><path d="M2 5 H28" stroke="#64748b" strokeWidth="1.6" strokeDasharray="4 3" /></svg> retour en arrière</span>
                    </div>
                </div>
                <div className="aide-fen-f">
                    <button type="button" className="aide-btn-sec" onClick={() => aide.setSignalerOuvert(true)}>
                        <i className="fa-solid fa-lightbulb" /> Proposer une amélioration
                    </button>
                    <button type="button" className="aide-btn-sec" onClick={fermer}>Fermer</button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default FenetreProcessus;
